import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, Mail, Route, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAutistaAuth } from "../../context/AutistaAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AreaLoginShell from "../../components/AreaLoginShell";
import { isNativeDriveApp } from "../../lib/drive/bridge";
import DriveThemeSwitch from "../../components/drive/DriveThemeSwitch";

const MARK_URL = "/images/logo-drive-mark.png";
const LOCKUP_URL = "/images/logo-drive.png";

export default function AutistaLogin() {
  const { loginWithPassword, loading: authLoading } = useAutistaAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginWithPassword(email, password);
      toast.success("Accesso effettuato");
      navigate("/autista", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#f6f5fb]" role="status" aria-label="Caricamento">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <AreaLoginShell
      accent="indigo"
      currentArea="autista"
      hideAreaLinks={isNativeDriveApp()}
      toggleClassName="fixed right-[max(0.75rem,env(safe-area-inset-right))] top-[max(0.75rem,env(safe-area-inset-top))] z-50"
      themeControl={<DriveThemeSwitch />}
      mainClassName="min-h-[100dvh] pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]"
      title="Area dedicata agli autisti"
      subtitle="Consulta il giro del giorno, i passeggeri e le fermate del centro diurno e dei trasporti."
      logo={
        <img
          src={LOCKUP_URL}
          alt="Drive by Affidya"
          className="mx-auto mb-3 h-11 w-auto lg:mx-0 lg:mb-9 lg:h-12"
        />
      }
      features={[
        {
          icon: Route,
          title: "Giro del giorno",
          text: "Fermate e passeggeri sempre aggiornati sul dispositivo.",
        },
        {
          icon: ShieldCheck,
          title: "Accesso riservato",
          text: "Solo gli autisti autorizzati dalla struttura possono entrare.",
        },
      ]}
    >
      <div className="mb-6 text-center">
        <img src={MARK_URL} alt="Drive by Affidya" className="mx-auto mb-3 h-12 w-12 rounded-2xl object-contain" />
        <h2 className="text-xl font-semibold tracking-tight text-slate-900">Accedi all&apos;area Autista</h2>
        <p className="mt-1 text-sm text-slate-500">Usa le credenziali fornite dalla struttura</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="autista-email" className="text-sm font-medium text-slate-700">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id="autista-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              placeholder="autista@esempio.it"
              className="h-12 rounded-xl border-slate-200 bg-slate-50/50 pl-10 text-base focus-visible:ring-indigo-500/30"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="autista-password" className="text-sm font-medium text-slate-700">
            Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id="autista-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              enterKeyHint="go"
              placeholder="••••••••"
              className="h-12 rounded-xl border-slate-200 bg-slate-50/50 pl-10 text-base focus-visible:ring-indigo-500/30"
            />
          </div>
        </div>
        <Button
          type="submit"
          className="h-12 w-full rounded-xl bg-indigo-600 text-base font-semibold text-white hover:bg-indigo-700 active:bg-indigo-800"
          disabled={loading}
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Accedi
        </Button>
      </form>
    </AreaLoginShell>
  );
}
